import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import Table from "../../../core/common/dataTable/index";
import { useGetTransactionsQuery, useGetInventoriesQuery } from "../api/starterKitApi";
import { formatDateToYMD } from "../../../core/utils/dateFormatter";
import { useSelector } from "react-redux";
import { selectUserRole, selectCurrentUser } from "../../../core/data/slice/authSlice";
import { useCenter } from "../../peoples/centers/hooks/useCenter";
import CommonSelect from "../../../core/common/commonSelect";
import CountUp from "react-countup";
import { useCourse } from "../../configuration/course/hooks/useCourse";

const StarterKitTransactionList = () => {
  const routes = all_routes;
  const userRole = useSelector(selectUserRole);
  const currentUser = useSelector(selectCurrentUser);
  const [page, setPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<{ type?: string; from_center_id?: number; to_center_id?: number; course_id?: number }>({});
  const [tempFilters, setTempFilters] = useState<{ type?: string; from_center_id?: number; to_center_id?: number; course_id?: number }>({});
  const dropdownMenuRef = useRef<HTMLDivElement | null>(null);

  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse } = useCenterList();
  const centers = centerResponse?.data?.items || [];

  const centerOptions = centers.map((center) => ({
    value: center.id.toString(),
    label: center.name,
  }));

  const { getCourseState } = useCourse();
  const { courses } = getCourseState();
  const courseOptions = courses.map((course: any) => ({
    value: course.id.toString(),
    label: course.name,
  }));

  const typeOptions = [
    { value: "assigned_to_main_center", label: "Stock Added (Main Center)" },
    { value: "assigned_to_center", label: "Assigned to Center" },
    { value: "assigned_to_student", label: "Assigned to Student" },
    { value: "returned_to_main_center", label: "Returned to Main Center" },
  ];

  const { data, isLoading, error } = useGetTransactionsQuery({
    page: page,
    size: recordsPerPage,
    search: search,
    to_center_id: userRole === "centeradmin" ? currentUser?.center_id : filters.to_center_id,
    from_center_id: userRole === "centeradmin" ? currentUser?.center_id : filters.from_center_id,
    type: filters.type,
    course_id: filters.course_id,
  });

  const { data: inventoryData } = useGetInventoriesQuery(
    userRole === "superadmin"
      ? { is_main_center_stock: 1 }
      : { center_id: currentUser?.center_id, is_main_center_stock: 0 }
  );
  const currentStocks = inventoryData?.data?.items || [];
  const totalAvailable = currentStocks.reduce((sum: number, item: any) => sum + item.available_stock, 0);
  const totalStock = currentStocks.reduce((sum: number, item: any) => sum + item.total_stock, 0);

  const transactions = data?.data?.items || [];
  const totalItems = data?.data?.totalItems || 0;

  const handlePageChange = (p: number, pageSize?: number) => {
    setPage(p);
    if (pageSize) {
      setRecordsPerPage(pageSize);
    }
  };

  const handleSearchChange = (text: string) => {
    setSearch(text);
    setPage(1);
  };

  const handleFilterChange = (filterName: string, value: any) => {
    setTempFilters(prev => ({
      ...prev,
      [filterName]: filterName === 'type' ? value?.value : (value?.value ? Number(value.value) : undefined)
    }));
  };

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({});
    setTempFilters({});
    setPage(1);
  };

  const columns = [
    {
      title: "S. No",
      render: (_: any, __: any, index: number) => index + 1 + (page - 1) * recordsPerPage,
    },
    {
      title: "Date",
      dataIndex: "assigned_on",
      render: (text: string) => formatDateToYMD(text),
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (text: string) => (
        <span className={`badge badge-soft-${text === 'assigned_to_student' ? 'info' : text === 'returned_to_main_center' ? 'warning' : 'secondary'} fs-12`}>
          {text.replace(/_/g, ' ').toUpperCase()}
        </span>
      ),
    },
    {
      title: "Course",
      dataIndex: "course",
      render: (course: any) => course?.name || "N/A",
    },
    {
      title: "From",
      render: (record: any) => {
        if (record.type === 'assigned_to_main_center') {
          return "Stock Added (Main Center)";
        }
        return record.from_main_center ? "Main Center" : (record.from_center?.name || "N/A");
      }
    },
    {
      title: "To (Center/Student)",
      render: (record: any) => {
        if (record.type === 'assigned_to_student') {
          return record.student ? record.student.name : "N/A";
        }
        if (record.type === 'returned_to_main_center' || record.type === 'assigned_to_main_center') {
          return "Main Center";
        }
        return record.to_center?.name || "N/A";
      }
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
    },
  ];

  const findOptionByValue = (options: any[], value: string | number | undefined) => {
    if (!value) return undefined;
    return options.find((option) => option.value === value.toString());
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Kit Distribution History</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">Inventory</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Transactions
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="row">
          <div className="col-xxl-4 col-md-4 col-sm-12 d-flex">
            <div className="card flex-fill animate-card border-0">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="avatar avatar-xl me-2 bg-primary-transparent p-1">
                    <i className="ti ti-package fs-24 text-primary"></i>
                  </div>
                  <div className="overflow-hidden flex-fill">
                    <div className="d-flex align-items-center justify-content-between">
                      <h2>
                        <CountUp end={totalStock} />
                      </h2>
                    </div>
                    <p>Overall Total Stock</p>
                  </div>
                </div>
                <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                  <span>Available: {totalAvailable}</span>
                  <span>Distributed: {totalStock - totalAvailable}</span>
                </div>
              </div>
            </div>
          </div>
          {courses.map((course: any) => {
            const stock = currentStocks.find((s: any) =>
              Number(s.course_id) === Number(course.id) || Number(s.course?.id) === Number(course.id)
            );
            const courseTotal = stock?.total_stock || 0;
            const courseAvailable = stock?.available_stock || 0;
            const courseDistributed = courseTotal - courseAvailable;
            
            return (
              <div className="col-xxl-4 col-md-4 col-sm-12 d-flex" key={course.id}>
                <div className="card flex-fill animate-card border-0">
                  <div className="card-body">
                    <div className="d-flex align-items-center">
                      <div className="avatar avatar-xl me-2 bg-success-transparent p-1">
                        <i className="ti ti-books fs-24 text-success"></i>
                      </div>
                      <div className="overflow-hidden flex-fill">
                        <div className="d-flex align-items-center justify-content-between">
                          <h2>
                            <CountUp end={courseTotal} />
                          </h2>
                        </div>
                        <p>{course.name || 'Unknown Course'} Stock</p>
                      </div>
                    </div>
                    <div className="d-flex justify-content-between mt-2 text-muted fs-13">
                      <span>Available: {courseAvailable}</span>
                      <span>Distributed: {courseDistributed}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">Transaction History</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="dropdown mb-3 me-2">
                <Link
                  to="#"
                  className="btn btn-outline-light bg-white dropdown-toggle"
                  data-bs-toggle="dropdown"
                  data-bs-auto-close="outside"
                  onClick={() => setTempFilters(filters)}
                >
                  <i className="ti ti-filter me-2" />
                  Filter
                </Link>
                <div className="dropdown-menu drop-width" ref={dropdownMenuRef}>
                  <form>
                    <div className="d-flex align-items-center border-bottom p-3">
                      <h4>Filter</h4>
                    </div>
                    <div className="p-3 pb-0 border-bottom">
                      <div className="row">
                        <div className="col-md-12">
                          <div className="mb-3">
                            <label className="form-label">Course</label>
                            <CommonSelect
                              className="select"
                              options={courseOptions}
                              value={findOptionByValue(courseOptions, tempFilters.course_id)}
                              onChange={(value) => handleFilterChange("course_id", value)}
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="mb-3">
                            <label className="form-label">Transaction Type</label>
                            <CommonSelect
                              className="select"
                              options={typeOptions}
                              value={findOptionByValue(typeOptions, tempFilters.type)}
                              onChange={(value) => handleFilterChange("type", value)}
                            />
                          </div>
                        </div>
                        {userRole === "superadmin" && (
                          <>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">From Center</label>
                                <CommonSelect
                                  className="select"
                                  options={centerOptions}
                                  value={findOptionByValue(centerOptions, tempFilters.from_center_id)}
                                  onChange={(value) => handleFilterChange("from_center_id", value)}
                                />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">To Center</label>
                                <CommonSelect
                                  className="select"
                                  options={centerOptions}
                                  value={findOptionByValue(centerOptions, tempFilters.to_center_id)}
                                  onChange={(value) => handleFilterChange("to_center_id", value)}
                                />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="p-3 d-flex align-items-center justify-content-end">
                      <button type="button" className="btn btn-light me-3" onClick={handleResetFilters}>
                        Reset
                      </button>
                      <button type="button" className="btn btn-primary" onClick={handleApplyFilters}>
                        Apply
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div className="card-body p-0 py-3">
            {isLoading ? (
              <div className="text-center">Loading transactions...</div>
            ) : error ? (
              <div className="alert alert-danger m-3">
                Error loading transactions.
              </div>
            ) : (
              <Table
                dataSource={transactions}
                columns={columns}
                Selection={false}
                onSearchChange={handleSearchChange}
                pagination={{
                  current: page,
                  pageSize: recordsPerPage,
                  total: totalItems,
                  onChange: handlePageChange,
                  pageSizeOptions: ["All", 10, 20, 30],
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StarterKitTransactionList;
